// src/services/demoApi/modelsAdminApi.ts
// Демо-API управления моделями (FR-3): реальный список из GET /llm/info/models
// с fallback на AVAILABLE_MODELS; добавленные вручную модели и флаги вкл/выкл —
// в localStorage. «Проверка соединения» — имитация (~1.5 с).
import { listLlmModels } from 'services/scenariosApi';
import { AVAILABLE_MODELS } from 'mocks/users';
import { MODEL_METRICS, DEFAULT_MODEL_HOSTS } from 'mocks/models';
import { demoDelay, readDemoStore, writeDemoStore } from './demoStorage';

export type ModelApiType = 'OpenAI-compatible' | 'vLLM' | 'Triton';

export interface AdminModel {
  id: string;
  name: string;
  host: string;
  port: number;
  apiType: ModelApiType;
  external: boolean;
  apiKey: string;
  enabled: boolean;
  latencyMs: number;
  tokens30d: number;
  source: 'backend' | 'custom';
}

export interface AddModelInput {
  name: string;
  host: string;
  port: number;
  apiType: ModelApiType;
  external: boolean;
  apiKey: string;
}

export interface UpdateModelInput {
  name: string;
  host: string;
  port: number;
  apiType: ModelApiType;
  external: boolean;
  apiKey: string;
}

export class DuplicateModelError extends Error {
  constructor(name: string) {
    super(`Модель «${name}» уже подключена`);
    this.name = 'DuplicateModelError';
  }
}

const CUSTOM_KEY = 'demo_custom_models';
const ACTIVE_KEY = 'demo_active_model'; // имя активной модели (одна одновременно) | null
const OVERRIDES_KEY = 'demo_model_overrides'; // originalBackendName → UpdateModelInput
const REMOVED_KEY = 'demo_models_removed'; // string[] originalBackendNames

interface StoredCustomModel {
  id: string;
  name: string;
  host: string;
  port: number;
  apiType: ModelApiType;
  external: boolean;
  apiKey: string;
}

function metricsFor(name: string): { latencyMs: number; tokens30d: number } {
  if (MODEL_METRICS[name]) return MODEL_METRICS[name];
  // Детерминированные «правдоподобные» метрики для добавленных моделей
  const seed = name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return { latencyMs: 80 + (seed % 320), tokens30d: 0 };
}

async function fetchBackendModelNames(): Promise<string[]> {
  try {
    const models = await listLlmModels();
    if (models && models.length > 0) return models.map((m) => m.name);
  } catch {
    // backend недоступен — ниже fallback
  }
  return AVAILABLE_MODELS.map((m) => m.value);
}

export async function listAdminModels(): Promise<AdminModel[]> {
  const backendNames = await fetchBackendModelNames();
  const custom = readDemoStore<StoredCustomModel[]>(CUSTOM_KEY, []);
  const overrides = readDemoStore<Record<string, UpdateModelInput>>(OVERRIDES_KEY, {});
  const removed = readDemoStore<string[]>(REMOVED_KEY, []);

  const backendModels: Omit<AdminModel, 'enabled'>[] = backendNames
    .filter((name) => !removed.includes(name))
    .map((originalName) => {
      const ov = overrides[originalName];
      const displayName = ov ? ov.name : originalName;
      return {
        id: `backend-${originalName}`,
        name: displayName,
        host: ov ? ov.host : (DEFAULT_MODEL_HOSTS[originalName]?.host ?? '10.12.4.20'),
        port: ov ? ov.port : (DEFAULT_MODEL_HOSTS[originalName]?.port ?? 8000),
        apiType: ov ? ov.apiType : 'OpenAI-compatible',
        external: ov ? ov.external : false,
        apiKey: ov ? ov.apiKey : '',
        ...metricsFor(displayName),
        source: 'backend' as const,
      };
    });

  const customModels: Omit<AdminModel, 'enabled'>[] = custom.map((m) => ({
    ...m,
    external: m.external ?? false,
    apiKey: m.apiKey ?? '',
    ...metricsFor(m.name),
    source: 'custom' as const,
  }));

  const all = [...backendModels, ...customModels];

  // Активна только одна модель. Если выбор ещё не делали — по умолчанию первая.
  const storedActive = readDemoStore<string | null | undefined>(ACTIVE_KEY, undefined);
  const active = storedActive === undefined ? (all[0]?.name ?? null) : storedActive;

  return all.map((m) => ({ ...m, enabled: m.name === active }));
}

/** Текущая активная модель (одна одновременно) или null, если активной нет. */
export async function getActiveModel(): Promise<AdminModel | null> {
  const models = await listAdminModels();
  return models.find((m) => m.enabled) ?? null;
}

/** Добавить модель: имитирует проверку соединения (~1.5 с), пишет в localStorage */
export async function addModel(input: AddModelInput): Promise<AdminModel> {
  const existing = await listAdminModels();
  if (existing.some((m) => m.name.toLowerCase() === input.name.trim().toLowerCase())) {
    throw new DuplicateModelError(input.name.trim());
  }
  await demoDelay(1500); // «Проверка соединения…»
  const stored: StoredCustomModel = {
    id: `custom-${Date.now()}`,
    name: input.name.trim(),
    host: input.host.trim(),
    port: input.port,
    apiType: input.apiType,
    external: input.external,
    apiKey: input.external ? input.apiKey.trim() : '',
  };
  const custom = readDemoStore<StoredCustomModel[]>(CUSTOM_KEY, []);
  writeDemoStore(CUSTOM_KEY, [...custom, stored]);
  return { ...stored, enabled: true, ...metricsFor(stored.name), source: 'custom' };
}

/** Активна одна модель. Включение делает её единственной активной; выключение снимает активность. */
export async function setModelEnabled(name: string, enabled: boolean): Promise<void> {
  await demoDelay(250);
  writeDemoStore<string | null>(ACTIVE_KEY, enabled ? name : null);
}

/** Обновить модель: custom — правит запись в demo_custom_models; backend — пишет в demo_model_overrides.
 *  При переименовании переносит флаг enabled на новое имя. */
export async function updateModel(originalName: string, input: UpdateModelInput): Promise<void> {
  await demoDelay(600);

  const existing = await listAdminModels();
  // Проверка дубля имени, исключая саму редактируемую модель (EC-14)
  const duplicate = existing.find(
    (m) => m.name.toLowerCase() === input.name.trim().toLowerCase() && m.name !== originalName
  );
  if (duplicate) {
    throw new DuplicateModelError(input.name.trim());
  }

  const custom = readDemoStore<StoredCustomModel[]>(CUSTOM_KEY, []);
  const customIdx = custom.findIndex((m) => m.name === originalName);

  if (customIdx !== -1) {
    // Custom-модель: правим запись на месте
    const updated = custom.map((m, i) =>
      i === customIdx
        ? {
            ...m,
            name: input.name.trim(),
            host: input.host.trim(),
            port: input.port,
            apiType: input.apiType,
            external: input.external,
            apiKey: input.external ? input.apiKey.trim() : '',
          }
        : m
    );
    writeDemoStore(CUSTOM_KEY, updated);
  } else {
    // Backend-модель: пишем override под исходным именем
    const overrides = readDemoStore<Record<string, UpdateModelInput>>(OVERRIDES_KEY, {});
    writeDemoStore(OVERRIDES_KEY, {
      ...overrides,
      [originalName]: {
        ...input,
        name: input.name.trim(),
        host: input.host.trim(),
        apiKey: input.external ? input.apiKey.trim() : '',
      },
    });
  }

  // Перенос активности если имя изменилось
  const newName = input.name.trim();
  if (newName !== originalName) {
    const active = readDemoStore<string | null | undefined>(ACTIVE_KEY, undefined);
    if (active === originalName) {
      writeDemoStore<string | null>(ACTIVE_KEY, newName);
    }
  }
}

/** Удалить модель. Custom — удаляет из demo_custom_models; Backend — добавляет исходное имя в demo_models_removed.
 *  Переименованная через override бэкенд-модель удаляется по отображаемому имени. */
export async function deleteModel(name: string): Promise<void> {
  await demoDelay(400);

  const custom = readDemoStore<StoredCustomModel[]>(CUSTOM_KEY, []);
  const customIdx = custom.findIndex((m) => m.name === name);

  if (customIdx !== -1) {
    writeDemoStore(
      CUSTOM_KEY,
      custom.filter((_, i) => i !== customIdx)
    );
    return;
  }

  // Backend-модель: найти исходное имя (может быть переименована через override)
  const overrides = readDemoStore<Record<string, UpdateModelInput>>(OVERRIDES_KEY, {});
  // Ищем originalName: либо сам name (без override), либо по override.name === name
  let originalName = name;
  for (const [orig, ov] of Object.entries(overrides)) {
    if (ov.name === name) {
      originalName = orig;
      break;
    }
  }

  const removed = readDemoStore<string[]>(REMOVED_KEY, []);
  if (!removed.includes(originalName)) {
    writeDemoStore(REMOVED_KEY, [...removed, originalName]);
  }
  // Очистить override если есть
  if (overrides[originalName]) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [originalName]: _removed, ...restOverrides } = overrides;
    writeDemoStore(OVERRIDES_KEY, restOverrides);
  }
}
